"use client";

import { useEffect, useState } from "react";
import type { ListingQuoteArgs } from "@convex/quotes/mutations/listingQuoteArgs.lib";
import { CodeEntryForm } from "@/components/auth/code-entry-form";
import { clearEmailedSignIn, readEmailedSignIn } from "@/lib/auth/emailed-sign-in";
import { openSignInModal } from "@/lib/auth/sign-in-modal-store";
import type { PendingGuestQuote } from "./pending-guest-quote-storage";
import type { GuestContactState } from "./listing-quote-request-state";
import type { useGuestQuoteSignIn } from "./use-guest-quote-sign-in";

export function useEmailedListingCode(
  restoresGuest: boolean,
  pending: PendingGuestQuote | null,
): string | undefined {
  const [emailed] = useState(() => (restoresGuest ? readEmailedSignIn("listing_quote") : null));
  const resumesPending = emailed !== null && pending?.email === emailed.email;
  useEffect(() => {
    if (!emailed) return;
    clearEmailedSignIn();
    if (!resumesPending) {
      openSignInModal({ view: "code", email: emailed.email, code: emailed.code });
    }
  }, [emailed, resumesPending]);
  return resumesPending ? emailed.code : undefined;
}

function GuestCodeIntro({ sentTo, emailedCode }: { sentTo: string; emailedCode?: string }) {
  return (
    <div className="space-y-1">
      <h3 className="text-sm font-semibold text-foreground">
        Confirm your email to send the request
      </h3>
      <p className="text-xs text-muted-foreground">
        {emailedCode
          ? `Confirm to send the request as ${sentTo}.`
          : `Enter the code we emailed to ${sentTo}.`}
      </p>
      <p className="text-xs text-muted-foreground">
        Your request is not sent until you confirm here.
      </p>
    </div>
  );
}

export function GuestCodeStep(props: {
  emailedCode?: string;
  contact: GuestContactState;
  guestSignIn: ReturnType<typeof useGuestQuoteSignIn>;
  buildRequest: () => ListingQuoteArgs;
}) {
  const { guestSignIn } = props;
  if (!guestSignIn.codeSentTo) return null;
  return (
    <CodeEntryForm
      initialCode={props.emailedCode}
      intro={<GuestCodeIntro sentTo={guestSignIn.codeSentTo} emailedCode={props.emailedCode} />}
      isVerifying={guestSignIn.isBusy}
      error={guestSignIn.error}
      submitLabel="Confirm and send request"
      busyLabel="Sending request…"
      onVerify={(code) => guestSignIn.verify(code, props.contact, props.buildRequest())}
      onResend={guestSignIn.resend}
      onChangeEmail={guestSignIn.changeEmail}
      onEdit={guestSignIn.clearError}
    />
  );
}
